import React, { useState } from 'react';
import theme from '@/data/theme';
import Modal from 'react-modal';
import { SeatAPIModel, SeatReservationStatus } from '@/apiModels/SeatReservation';
import {
  StyledBottomButtonWrapper,
  StyledFilledBorderButton,
  StyledInputField,
  StyledInputLabel,
  StyledModalHeader,
  StyledModalTitleWrapper,
  StyledSavedButton,
  StyledTextAreaField,
  TableBookingDetailPhoneInputWrapper,
} from '@/components/ui/Modals/EditTableModal/EditTableModal.styled.ts';
import ModalCloseButton from '@/components/ui/ModalCloseButton';
import SeatStatusPayload, { SeatStatus } from '@/apiModels/SeatStatusPayload';
import { toast } from 'react-toastify';
import useUpdateEventSeat from '@/hooks/useUpdateEventSeat.ts';
import { useQueryClient } from '@tanstack/react-query';
import PhoneInput from 'react-phone-number-input';
import { StyledInputFieldError } from '@/components/ui/PersonalDetailEntry/PersonalDetailEntry.styled.ts';
import LoadingSplash from '@/components/ui/LoadingScreen';

export interface BookingFormData {
  fullname: string;
  phone_number?: string;
  email?: string;
  remarks?: string;
  pax?: number;
  status?: SeatStatusPayload;
}

interface ITableBookingProps {
  isOpen: boolean;
  onClose: () => void;
  seatInformation: SeatAPIModel;
  eventId: string;
  seatId: string;
  onSuccess: (bookingData: BookingFormData) => void;
  isLoading: boolean;
}

const TableBookingModal: React.FunctionComponent<ITableBookingProps> = ({
  isOpen,
  onClose,
  seatInformation,
  eventId,
  seatId,
  onSuccess,
  isLoading,
}) => {
  const [formData, setFormData] = useState<BookingFormData>({
    fullname: '',
    phone_number: '',
    email: '',
    remarks: '',
  });
  const [emptyFullNameError, setEmptyFullNameError] = useState('');
  const [emptyPhoneNumberError, setEmptyPhoneNumberError] = useState('');
  const [emptyEmailError, setEmptyEmailError] = useState('');
  const [invalidEmailTypeError, setInvalidEmailTypeError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();

  const { mutate: updateSeatStatus } = useUpdateEventSeat({
    cb: () => {},
    cbSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seat'] }).then(() => {});
      onSuccess(formData);
      toast.success(`You have successfully booked ${seatInformation.serial_no} Seat.`);
      setIsSubmitting(false);
      handleClose();
    },
    cbError: (error?: CustomError<RevelErrorResponse>) => {
      setIsSubmitting(false);

      if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;

        if (errors[0]?.field === 'reservation_details' && errors[0]?.message) {
          const reservationErrors = errors[0].message as unknown as {
            fullname?: string[];
            phone_number?: string[];
            email?: string[];
          };

          if (reservationErrors.fullname) {
            setEmptyFullNameError(reservationErrors.fullname[0] || 'Enter a valid full-name.');
          }
          if (reservationErrors.phone_number) {
            setEmptyPhoneNumberError(
              reservationErrors.phone_number[0] || 'Enter a valid phone number.',
            );
          }
          if (reservationErrors.email) {
            setInvalidEmailTypeError(reservationErrors.email[0] || 'Invalid email address.');
          }
        }
      } else {
        toast.error('Failed to book the seat. Please try again.');
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'fullname' && value.trim()) {
      setEmptyFullNameError('');
    }
    if (name === 'email' && value.trim()) {
      setInvalidEmailTypeError('');
      setEmptyEmailError('');
    }
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData((prev) => ({
      ...prev,
      phone_number: value || '',
    }));

    if (value?.trim()) {
      setEmptyPhoneNumberError('');
    }
  };

  const handleClose = () => {
    setFormData({
      fullname: '',
      phone_number: '',
      email: '',
      remarks: '',
    });

    setEmptyFullNameError('');
    setEmptyPhoneNumberError('');
    setEmptyEmailError('');
    setInvalidEmailTypeError('');
    setIsSubmitting(false);
    onClose();
  };

  const buildReservationDetails = () => {
    const details: Record<string, string> = {
      fullname: formData.fullname,
      status: SeatStatus.BOOKED,
    };

    if (formData.email?.trim()) {
      details.email = formData.email;
    }

    if (formData.phone_number?.trim()) {
      details.phone_number = formData.phone_number;
    }

    if (formData.remarks?.trim()) {
      details.remarks = formData.remarks;
    }

    return details;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
  };

  const handleSeatAction = () => {
    setEmptyFullNameError('');
    setEmptyPhoneNumberError('');
    setEmptyEmailError('');
    setInvalidEmailTypeError('');

    let hasError = false;

    if (!formData.fullname.trim()) {
      setEmptyFullNameError('Enter a valid full-name.');
      hasError = true;
    }

    if (formData.email && formData.email.trim()) {
      if (!validateEmail(formData.email)) {
        setInvalidEmailTypeError('Invalid email address');
        hasError = true;
      }
    }

    if (hasError) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    if (seatInformation.status === SeatReservationStatus.AVAILABLE) {
      setIsSubmitting(true);

      const payload = {
        seat: seatInformation.id,
        price: seatInformation.price,
        features: seatInformation.features,
        reservation_details: buildReservationDetails(),
      } as unknown as SeatStatusPayload;

      updateSeatStatus({
        eventId,
        seatId,
        payload,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={false}
      style={{
        content: {
          maxWidth: '320px',
          minWidth: '296px',
          height: 'fit-content',
          borderRadius: '10px',
          padding: '25px 10px',
          inset: '0',
          margin: 'auto',
          boxShadow: '0px 2px 6px 0px rgba(134, 92, 208, 0.50)',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        },
        overlay: {
          background: theme.colors.modalBackground,
        },
      }}
    >
      <LoadingSplash
        isVisible={isSubmitting}
        text={`Booking ${seatInformation.serial_no}...`}
        spinnerColor={theme.colors.primary}
        backgroundColor="rgba(255, 255, 255, 0.95)"
        textColor={theme.colors.primary}
        spinnerSize="40px"
        borderRadius="10px"
      />

      <StyledModalTitleWrapper>
        <StyledModalHeader>Table:&nbsp;{seatInformation.serial_no}</StyledModalHeader>
        <ModalCloseButton onClose={handleClose} />
      </StyledModalTitleWrapper>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
        <StyledInputLabel>
          Booked For <span style={{ color: '#EF4444' }}>*</span>
        </StyledInputLabel>
        <StyledInputField
          type="text"
          name="fullname"
          placeholder="Enter Name"
          value={formData.fullname}
          onChange={handleInputChange}
          $emptyError={emptyFullNameError}
          disabled={isSubmitting}
        />
        {emptyFullNameError && <StyledInputFieldError>{emptyFullNameError}</StyledInputFieldError>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
        <StyledInputLabel>Phone Number</StyledInputLabel>
        <TableBookingDetailPhoneInputWrapper $emptyError={emptyPhoneNumberError}>
          <PhoneInput
            name="phone_number"
            placeholder="Enter Phone Number"
            value={formData.phone_number}
            defaultCountry={'NP'}
            onChange={handlePhoneChange}
            disabled={isSubmitting}
          />
        </TableBookingDetailPhoneInputWrapper>
        {emptyPhoneNumberError && (
          <StyledInputFieldError>{emptyPhoneNumberError}</StyledInputFieldError>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
        <StyledInputLabel>Email</StyledInputLabel>
        <StyledInputField
          type="email"
          name="email"
          placeholder="Enter Email Address"
          value={formData.email}
          onChange={handleInputChange}
          $emptyError={emptyEmailError || invalidEmailTypeError}
          disabled={isSubmitting}
        />
        {emptyEmailError && <StyledInputFieldError>{emptyEmailError}</StyledInputFieldError>}
        {!emptyEmailError && invalidEmailTypeError && (
          <StyledInputFieldError>{invalidEmailTypeError}</StyledInputFieldError>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
        <StyledInputLabel>Remarks</StyledInputLabel>
        <StyledTextAreaField
          name="remarks"
          placeholder="Write Remarks..."
          value={formData.remarks}
          onChange={handleInputChange}
          disabled={isSubmitting}
        />
      </div>

      <StyledBottomButtonWrapper>
        <StyledFilledBorderButton onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </StyledFilledBorderButton>
        <StyledSavedButton onClick={handleSeatAction} disabled={isLoading || isSubmitting}>
          {isSubmitting ? 'Booking...' : 'Book'}
        </StyledSavedButton>
      </StyledBottomButtonWrapper>
    </Modal>
  );
};

export default TableBookingModal;
